import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  Spinner,
  Modal,
  Table,
} from "react-bootstrap";
import paymentService from "../../services/paymentService";
import { toast } from "react-toastify";
import {
  FaDownload,
  FaSearch,
  FaWallet,
  FaCreditCard,
  FaMoneyBillWave,
  FaUniversity,
  FaCalendarAlt,
  FaUndo,
  FaFilter,
  FaStore,
} from "react-icons/fa";
import InfoPill from "../Common/InfoPill";
import CopyableId from "../Common/CopyableId";
import refundService from "../../services/refundService";
import "../Reports/Report.css";
import {
  formatDateTimeToDDMMYYYY12Hr,
} from "../../utils/dateUtils";
import DateRangePicker from "rsuite/DateRangePicker";
import "rsuite/DateRangePicker/styles/index.css";
const PaymentHistory = () => {
  const { user } = useSelector((state) => state.auth);
  const isVendor = user?.user_type === "vendor";
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    payment_status: "",
    payment_mode: "",
    start_date: "",
    end_date: "",
    search: "", // For searching by Payment ID or Order ID
  });
  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const date = new Date(year, month, day);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  };

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const start = parseDateString(filters.start_date);
  const end = parseDateString(filters.end_date);
  const dateRangeValue = (start && end) ? [start, end] : null;

  const handleDateRangeChange = (range) => {
    if (range && range[0] && range[1]) {
      setFilters({
        ...filters,
        start_date: formatDateToYYYYMMDD(range[0]),
        end_date: formatDateToYYYYMMDD(range[1]),
      });
    } else {
      setFilters({
        ...filters,
        start_date: "",
        end_date: "",
      });
    }
  };

  // Refund modal state
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [existingRefunds, setExistingRefunds] = useState([]);
  const [refundForm, setRefundForm] = useState({
    order_id: "",
    payment_id: "",
    refund_amount: "",
    refund_mode: "cash",
    refund_type: "manual",
    reason: "",
    notes: "",
  });
  const [refundLoading, setRefundLoading] = useState(false);

  // Fetch payments
  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await paymentService.getAllPayments(filters);
      if (response.success) {
        setPayments(response.data || []);
        setSummary(response.summary || null);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch payments");
      toast.error("Failed to load payment history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Apply filters
  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleApplyFilters = () => {
    fetchPayments();
  };

  const handleResetFilters = () => {
    setFilters({
      payment_status: "",
      payment_mode: "",
      start_date: "",
      end_date: "",
      search: "",
    });
    // setTimeout(() => fetchPayments(), 100);
  };

  // Download invoice
  const handleDownloadInvoice = async (orderId, paymentStatus, orderStatus) => {
    if (!orderId) {
      toast.error("Order ID not available");
      return;
    }

    if (
      paymentStatus?.toLowerCase() === "cancelled" ||
      orderStatus?.toLowerCase() === "cancelled"
    ) {
      toast.warning("Invoice cannot be generated for cancelled orders.");
      return;
    }

    try {
      const blob = await paymentService.downloadInvoice(orderId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Invoice downloaded successfully");
    } catch (err) {
      // Show specific error message from backend
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to download invoice";
      toast.error(errorMessage);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Handle open refund modal
  const handleOpenRefundModal = async (payment) => {
    const normalizedPaymentStatus =
      payment?.payment_status?.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");
    if (normalizedPaymentStatus === "refunded" || payment?.refund_status === "full") {
      toast.info("This payment has already been refunded.");
      return;
    }

    setSelectedPayment(payment);

    setRefundForm({
      order_id: payment.order_id || "",
      payment_id: payment.id || "",
      refund_amount: payment.amount || "",
      refund_mode: payment.payment_mode === "online" ? "online" : "cash",
      refund_type: "manual",
      reason: "",
      notes: "",
    });

    // Fetch existing refunds for this payment
    try {
      const response = await refundService.getRefundsByPayment(payment.id);
      setExistingRefunds(response.data || []);
    } catch (error) {
      console.error("Error fetching refunds:", error);
      setExistingRefunds([]);
    }

    setShowRefundModal(true);
  };

  // Handle create refund
  const handleCreateRefund = async () => {
    try {
      if (!refundForm.payment_id) {
        toast.error("Payment ID is required");
        return;
      }
      if (!refundForm.refund_amount || refundForm.refund_amount <= 0) {
        toast.error("Please enter a valid refund amount");
        return;
      }

      setRefundLoading(true);

      if (refundForm.refund_type === "automatic") {
        await refundService.createAutomaticRefund({
          payment_id: refundForm.payment_id,
          refund_amount: parseFloat(refundForm.refund_amount),
          reason: refundForm.reason,
          notes: refundForm.notes,
        });
        toast.success("Automatic refund processed successfully");
      } else {
        await refundService.createManualRefund({
          order_id: refundForm.order_id || null,
          payment_id: refundForm.payment_id || null,
          refund_amount: parseFloat(refundForm.refund_amount),
          refund_mode: refundForm.refund_mode,
          reason: refundForm.reason,
          notes: refundForm.notes,
        });
        toast.success("Manual refund created successfully");
      }

      setShowRefundModal(false);
      setRefundForm({
        order_id: "",
        payment_id: "",
        refund_amount: "",
        refund_mode: "cash",
        refund_type: "manual",
        reason: "",
        notes: "",
      });
      setSelectedPayment(null);
      setExistingRefunds([]);

      // Refresh payments list
      fetchPayments();
    } catch (error) {
      console.error("Error creating refund:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to create refund";

      // Check if it's a test payment or invalid payment ID error
      if (
        errorMessage.includes("test") ||
        errorMessage.includes("placeholder") ||
        errorMessage.includes("Invalid or test") ||
        errorMessage.includes("not found in Razorpay")
      ) {
        toast.error(
          <div>
            <div>{errorMessage}</div>
            <div style={{ marginTop: "5px", fontSize: "0.9em" }}>
              <strong>Suggestion:</strong> Switch to "Manual" refund type for
              this payment.
            </div>
          </div>,
          { autoClose: 8000 }
        );

        // Auto-switch to manual refund if automatic fails
        if (refundForm.refund_type === "automatic") {
          setRefundForm({
            ...refundForm,
            refund_type: "manual",
            refund_mode: "cash",
          });
          toast.info(
            "Switched to Manual refund. Please review and submit again."
          );
        }
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setRefundLoading(false);
    }
  };

  // Get refund status badge
  const getRefundStatusBadge = (status) => {
    if (!status) return <span className="text-muted">-</span>;
    const statusMap = {
      pending: { variant: "warning", text: "Pending" },
      processing: { variant: "info", text: "Processing" },
      completed: { variant: "success", text: "Completed" },
      failed: { variant: "danger", text: "Failed" },
      cancelled: { variant: "secondary", text: "Cancelled" },
      refunded: { variant: "dark", text: "Refunded" },
      partially_refunded: { variant: "warning", text: "Partially Refunded" },
    };
    const key = status.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");
    const statusInfo = statusMap[key] || {
      variant: "secondary",
      text: formatStatusText(status),
    };
    return <Badge bg={statusInfo.variant}>{statusInfo.text}</Badge>;
  };

  // Format date with DD-MM-YYYY and 12hr time format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return formatDateTimeToDDMMYYYY12Hr(dateString);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    if (!status) return <span className="text-muted">-</span>;
    const statusMap = {
      completed: { variant: "success", text: "Completed" },
      captured: { variant: "success", text: "Captured" },
      paid: { variant: "success", text: "Paid" },
      pending: { variant: "warning", text: "Pending" },
      failed: { variant: "danger", text: "Failed" },
      cancelled: { variant: "secondary", text: "Cancelled" },
      refunded: { variant: "dark", text: "Refunded" },
      partially_refunded: { variant: "warning", text: "Partially Refunded" },
      unpaid: { variant: "danger", text: "Unpaid" },
    };
    const key = status.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");
    const statusInfo = statusMap[key] || {
      variant: "secondary",
      text: formatStatusText(status),
    };
    return <Badge bg={statusInfo.variant}>{statusInfo.text}</Badge>;
  };

  // Table headers
  const headers = [
    {
      prop: "invoice_number",
      title: "Invoice #",
      isSortable: true,
      isFilterable: true,
      cell: (row) => {
        const invNumber =
          row.invoice_number ||
          (row.id ? `INV-${row.id.substring(0, 8).toUpperCase()}` : "-");
        return (
          <span
            style={{
              fontFamily: "monospace",
              fontSize: "0.9em",
              fontWeight: "600",
              color: "#3b82f6",
              whiteSpace: "nowrap",
            }}
          >
            {invNumber}
          </span>
        );
      },
    },
    {
      prop: "id",
      title: "Payment ID",
      isSortable: true,
      isFilterable: true,
      cell: (row) => <CopyableId id={row.id} label="Payment ID" />,
    },
    {
      prop: "order_no",
      title: "Order No",
      isSortable: true,
      isFilterable: true,
      cell: (row) => {
        const orderDisplay =
          row.order_no ||
          (row.order_id ? `ORD-${row.order_id.substring(0, 8).toUpperCase()}` : "-");
        return row.order_id ? (
          <Link
            to={`/admin/orders/details/${row.order_id}`}
            style={{ textDecoration: "none" }}
            title={`View Order Details for ${orderDisplay}`}
          >
            <span
              style={{
                fontFamily: "monospace",
                fontSize: "0.9em",
                fontWeight: "700",
                color: "#2563eb",
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: "4px",
                padding: "2px 6px",
                whiteSpace: "nowrap",
              }}
            >
              {orderDisplay}
            </span>
          </Link>
        ) : (
          <span className="text-muted">N/A</span>
        );
      },
    },
    {
      title: "Customer Phone",
      prop: "phone",
      isSortable: true,
      isFilterable: true,
      // cell: (row) =>
      //   row.first_name || row.last_name
      //     ? `${row.first_name || ""} ${row.last_name || ""}`.trim()
      //     : "N/A",
    },
    {
      prop: "amount",
      title: isVendor ? "Your Amount" : "Amount",
      isSortable: true,
      isFilterable: true,
      cell: (row) => {
        const displayAmount = isVendor ? (row.vendor_amount || row.amount) : row.amount;
        const hasDiff = isVendor && row.amount && parseFloat(row.amount) > parseFloat(displayAmount);
        return (
          <div>
            <span style={{ fontWeight: "bold", color: "#10b981" }}>
              {formatCurrency(displayAmount)}
            </span>
            {hasDiff && (
              <div
                className="text-muted"
                style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}
                title="Customer total paid for order across all vendors"
              >
                Order Total: {formatCurrency(row.amount)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      prop: "order_status",
      title: "Order Status",
      isSortable: true,
      isFilterable: true,
      cell: (row) => {
        if (!row.order_status) return <span className="text-muted">-</span>;
        const statusMap = {
          placed: { variant: "warning", text: "Placed" },
          accepted: { variant: "info", text: "Accepted" },
          confirmed: { variant: "info", text: "Confirmed" },
          processing: { variant: "warning", text: "Processing" },
          packed: { variant: "primary", text: "Packed" },
          out_for_delivery: { variant: "primary", text: "Out For Delivery" },
          shipped: { variant: "info", text: "Shipped" },
          delivered: { variant: "success", text: "Delivered" },
          cancelled: { variant: "danger", text: "Cancelled" },
          returned: { variant: "secondary", text: "Returned" },
          refunded: { variant: "dark", text: "Refunded" },
        };
        const key = row.order_status.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");
        const statusInfo = statusMap[key] || {
          variant: "secondary",
          text: formatStatusText(row.order_status),
        };
        return <Badge bg={statusInfo.variant}>{statusInfo.text}</Badge>;
      },
    },
    {
      prop: "payment_status",
      title: "Payment Status",
      isSortable: true,
      isFilterable: true,
      cell: (row) => getStatusBadge(row.payment_status),
    },
    {
      prop: "payment_mode",
      title: "Mode",
      isSortable: true,
      isFilterable: true,
      cell: (row) => {
        if (!row.payment_mode) return <span className="text-muted">-</span>;
        const key = row.payment_mode.toString().toLowerCase().trim();
        const variant = key.includes("online") ? "primary" : "info";
        return (
          <Badge bg={variant}>
            {formatStatusText(row.payment_mode)}
          </Badge>
        );
      },
    },
    {
      prop: "transaction_id",
      title: "Transaction ID",
      isSortable: true,
      isFilterable: true,
      cell: (row) => {
        const txnId =
          row.transaction_id ||
          (row.id ? `TXN-${row.id.substring(0, 8).toUpperCase()}` : "-");
        return <CopyableId id={txnId} label="Transaction ID" truncateLength={15} />;
      },
    },
    {
      prop: "transaction_date",
      title: "Date",
      isSortable: true,
      cell: (row) => formatDate(row.transaction_date || row.created_at),
    },
    {
      prop: "actions",
      title: "Actions",
      isSortable: false,
      cell: (row) => {
        const normalizedPaymentStatus =
          row.payment_status?.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");
        const normalizedOrderStatus =
          row.order_status?.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");

        const isCancelled =
          normalizedPaymentStatus === "cancelled" ||
          normalizedOrderStatus === "cancelled";
        const isRefunded =
          normalizedPaymentStatus === "refunded" ||
          row.refund_status === "full";

        const canDownloadInvoice = row.order_id && !isCancelled;
        const canRefund = !isRefunded;

        return (
          <div style={{ display: "flex", gap: "5px" }}>
            {row.order_id && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() =>
                  handleDownloadInvoice(
                    row.order_id,
                    row.payment_status,
                    row.order_status
                  )
                }
                title={
                  canDownloadInvoice
                    ? "Download Invoice"
                    : "Invoice not available for cancelled orders"
                }
                disabled={!canDownloadInvoice}
                style={{
                  padding: "4px 8px",
                  opacity: canDownloadInvoice ? 1 : 0.5,
                  cursor: canDownloadInvoice ? "pointer" : "not-allowed",
                }}
              >
                <FaDownload />
              </Button>
            )}
            {!isVendor && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleOpenRefundModal(row)}
                title={isRefunded ? "Payment already refunded" : "Create Refund"}
                disabled={!canRefund}
                style={{
                  padding: "4px 8px",
                  cursor: canRefund ? "pointer" : "not-allowed",
                }}
              >
                <FaUndo />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const formatStatusText = (status) => {
    if (!status) return "";
    const map = {
      paid: "Paid",
      captured: "Captured",
      completed: "Completed",
      pending: "Pending",
      failed: "Failed",
      cancelled: "Cancelled",
      refunded: "Refunded",
      partially_refunded: "Partially Refunded",
      placed: "Placed",
      confirmed: "Confirmed",
      processing: "Processing",
      shipped: "Shipped",
      delivered: "Delivered",
      returned: "Returned",
      manual: "Manual",
      automatic: "Automatic",
      partial: "Partial",
      cash: "Cash",
      online: "Online",
      wallet: "Wallet",
      bank_transfer: "Bank Transfer",
    };
    return (
      map[status.toString().toLowerCase()] ||
      status
        .toString()
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ")
    );
  };

  // Prepare table data
  const tableData = payments.map((payment) => ({
    ...payment,
    order_no: payment.order_no || (payment.order_id ? `ORD-${payment.order_id.substring(0, 8).toUpperCase()}` : ""),
    payment_status: formatStatusText(payment.payment_status),
    payment_mode: formatStatusText(payment.payment_mode),
    order_status: formatStatusText(payment.order_status),
    customer_name: `${payment.first_name || ""} ${payment.last_name || ""}`.trim(),
  }));

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading payment history...</p>
        </div>
      </div>
    );
  }

  const labels = {
    beforeSelect: " ",
  };

  return (
    <div className="page-container">
      {/* Breadcrumb */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center flex-wrap gap-2">
          <Link className="breadcrum" to={isVendor ? "/vendor/dashboard" : "/"}>
            {isVendor ? "Vendor Portal" : "Home"}
            <i
              className="fa-solid fa-chevron-right"
              style={{ marginLeft: "4px", marginRight: "4px" }}
            ></i>{" "}
            Payment History
          </Link>
          {isVendor && (
            <Badge
              bg="info"
              className="d-inline-flex align-items-center px-2 py-1"
              style={{ fontSize: "0.75rem", letterSpacing: "0.3px" }}
            >
              <FaStore className="me-1" /> Vendor Portal View
            </Badge>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <Row className="g-3 mb-4">
          <Col xl={3} md={6}>
            <div className="stat-card shadow-sm h-100">
              <div className="stat-header">
                <div className="stat-icon star-icon-bg-color1">
                  <FaCreditCard />
                </div>
                <div className="stat-change">
                  <h3 className="stat-title">{isVendor ? "Your Orders Paid" : "Total Payments"}</h3>
                  <p className="stat-value stat-value-color1">
                    {summary.total_payments || 0}
                  </p>
                </div>
              </div>
            </div>
          </Col>
          <Col xl={3} md={6}>
            <div className="stat-card shadow-sm h-100">
              <div className="stat-header">
                <div className="stat-icon star-icon-bg-color2">
                  <FaWallet />
                </div>
                <div className="stat-change">
                  <h3 className="stat-title">{isVendor ? "Your Collected Earnings" : "Total Earnings"}</h3>
                  <p className="stat-value stat-value-color2">
                    {formatCurrency(
                      summary.total_collected || 0
                    )}
                  </p>
                </div>
              </div>
            </div>
          </Col>
          <Col xl={3} md={6}>
            <div className="stat-card shadow-sm h-100">
              <div className="stat-header">
                <div className="stat-icon star-icon-bg-color3">
                  <FaMoneyBillWave />
                </div>
                <div className="stat-change">
                  <h3 className="stat-title">{isVendor ? "Pending Payout" : "Pending Amount"}</h3>
                  <p className="stat-value stat-value-color3">
                    {formatCurrency(summary.total_pending || 0)}
                  </p>
                </div>
              </div>
            </div>
          </Col>
          <Col xl={3} md={6}>
            <div className="stat-card shadow-sm h-100">
              <div className="stat-header">
                <div className="stat-icon star-icon-bg-color4">
                  <FaUniversity />
                </div>
                <div className="stat-change">
                  <h3 className="stat-title">{isVendor ? "Your Total Sales" : "Total Amount"}</h3>
                  <p className="stat-value stat-value-color4">
                    {formatCurrency(summary.total_amount || 0)}
                  </p>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger shadow-sm" role="alert">
          {error}
        </div>
      )}

      {/* Datatable */}
      <DatatableWrapper
        body={tableData}
        headers={headers}
        paginationOptionsProps={{
          initialState: {
            rowsPerPage: 10,
            options: [5, 10, 15, 20, 50, 100],
          },
        }}
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div className="d-flex align-items-center gap-2 flex-grow-1 flex-wrap">
            {/* Search Filter */}
            <div style={{ minWidth: "200px", maxWidth: "260px" }} className="flex-grow-1">
              <Filter
                placeholder="Search Order No / Payment ID..."
                className="w-100 btn-success"
              />
            </div>

            {/* Payment Status Filter */}
            <div style={{ width: "125px" }}>
              <Form.Select
                size="sm"
                value={filters.payment_status}
                onChange={(e) =>
                  handleFilterChange("payment_status", e.target.value)
                }
              >
                <option value="">All Status</option>
                <option value="paid">Paid</option>
                <option value="captured">Captured</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="refunded">Refunded</option>
                <option value="failed">Failed</option>
                <option value="cancelled">Cancelled</option>
              </Form.Select>
            </div>

            {/* Payment Mode Filter */}
            <div style={{ width: "115px" }}>
              <Form.Select
                size="sm"
                value={filters.payment_mode}
                onChange={(e) =>
                  handleFilterChange("payment_mode", e.target.value)
                }
              >
                <option value="">All Modes</option>
                <option value="online">Online</option>
                <option value="cash">Cash (COD)</option>
              </Form.Select>
            </div>

            {/* Date Range Filter */}
            <div style={{ minWidth: "240px" }} className="rsuite-daterange-picker-wrapper">
              <DateRangePicker
                value={dateRangeValue}
                onChange={handleDateRangeChange}
                character=" – "
                placeholder="Select Date Range"
                size="sm"
                style={{ width: "100%" }}
                className="payment-daterange-picker"
              />
            </div>

            {(filters.payment_status ||
              filters.payment_mode ||
              filters.start_date ||
              filters.end_date ||
              filters.search) && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleResetFilters}
                >
                  Reset
                </Button>
              )}

            <PaginationOptions labels={labels} />
            <InfoPill left={isVendor ? "Your Payments" : "Total Payments"} right={payments.length} />
          </div>
        </div>

        {/* Table */}
        <div
          className="table-responsive shadow-sm"
          style={{ position: "relative" }}
        >
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                borderRadius: "4px",
              }}
            >
              <Spinner animation="border" variant="success" />
            </div>
          )}
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        {/* Pagination */}
        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination
            classes={{
              button: "bg-dark",
            }}
          />
        </div>
      </DatatableWrapper>
      {/* Refund Modal */}
      <Modal
        show={showRefundModal}
        onHide={() => {
          setShowRefundModal(false);
          setSelectedPayment(null);
          setExistingRefunds([]);
          setRefundForm({
            order_id: "",
            payment_id: "",
            refund_amount: "",
            refund_mode: "cash",
            refund_type: "manual",
            reason: "",
            notes: "",
          });
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUndo className="me-2 text-danger" />
            Create Refund
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPayment && (
            <div className="mb-3 p-3 bg-light rounded shadow-sm border">
              <h6 className="fw-bold mb-2">Payment Details</h6>
              <p className="mb-1">
                <strong>Payment ID:</strong>{" "}
                <CopyableId id={selectedPayment.id} label="Payment ID" />
              </p>
              <p className="mb-1">
                <strong>Amount:</strong>{" "}
                <span className="fw-bold text-success">{formatCurrency(selectedPayment.amount)}</span>
              </p>
              <p className="mb-1">
                <strong>Payment Mode:</strong>{" "}
                <Badge bg={selectedPayment.payment_mode?.toLowerCase() === "online" ? "primary" : "info"}>
                  {selectedPayment.payment_mode
                    ? selectedPayment.payment_mode.charAt(0).toUpperCase() + selectedPayment.payment_mode.slice(1).toLowerCase()
                    : "N/A"}
                </Badge>
              </p>
              <p className="mb-0">
                <strong>Payment Status:</strong>{" "}
                {getStatusBadge(selectedPayment.payment_status)}
              </p>
            </div>
          )}

          {/* Existing Refunds */}
          {existingRefunds.length > 0 && (
            <div className="mb-3">
              <h6 className="fw-bold mb-2">Existing Refunds</h6>
              <Table striped bordered hover size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th>Refund ID</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Type</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {existingRefunds.map((refund) => (
                    <tr key={refund.id}>
                      <td>
                        <CopyableId id={refund.id} label="Refund ID" />
                      </td>
                      <td className="fw-bold">{formatCurrency(refund.refund_amount)}</td>
                      <td>{getRefundStatusBadge(refund.refund_status)}</td>
                      <td>
                        <Badge
                          bg={
                            refund.refund_type === "automatic"
                              ? "success"
                              : "primary"
                          }
                        >
                          {refund.refund_type
                            ? refund.refund_type.charAt(0).toUpperCase() + refund.refund_type.slice(1).toLowerCase()
                            : "-"}
                        </Badge>
                      </td>
                      <td>{formatDateTimeToDDMMYYYY12Hr(refund.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Refund Type *</Form.Label>
                  <Form.Select
                    value={refundForm.refund_type}
                    onChange={(e) =>
                      setRefundForm({
                        ...refundForm,
                        refund_type: e.target.value,
                      })
                    }
                  >
                    <option value="manual">Manual</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Refund Amount *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    max={selectedPayment?.amount || ""}
                    placeholder="Enter refund amount"
                    value={refundForm.refund_amount}
                    onChange={(e) =>
                      setRefundForm({
                        ...refundForm,
                        refund_amount: e.target.value,
                      })
                    }
                  />
                  {selectedPayment && (
                    <Form.Text className="text-muted">
                      Payment Amount: {formatCurrency(selectedPayment.amount)}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              {refundForm.refund_type === "manual" && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Refund Mode</Form.Label>
                    <Form.Select
                      value={refundForm.refund_mode}
                      onChange={(e) =>
                        setRefundForm({
                          ...refundForm,
                          refund_mode: e.target.value,
                        })
                      }
                    >
                      <option value="cash">Cash</option>
                      <option value="online">Online</option>
                      <option value="wallet">Wallet</option>
                      <option value="bank_transfer">Bank Transfer</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Reason *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter refund reason"
                    value={refundForm.reason}
                    onChange={(e) =>
                      setRefundForm({ ...refundForm, reason: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Additional notes (optional)"
                    value={refundForm.notes}
                    onChange={(e) =>
                      setRefundForm({ ...refundForm, notes: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowRefundModal(false);
              setSelectedPayment(null);
              setExistingRefunds([]);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateRefund}
            disabled={refundLoading}
          >
            {refundLoading ? "Processing..." : "Create Refund"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PaymentHistory;
